import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Switch,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Badge,
  HStack,
  VStack,
  Select,
  Spinner,
  Textarea,
  Input,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, CheckCircleIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomButton, CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";
import CustomTables, { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";
import { MultiSelectMenu } from "../../../../components/MultiSelectMenu/MultiSelectMenu";
import { HrlenseInput } from "../../../../components/Input/Input";
import {
  ICrmMapping,
  emptyCrmMapping,
  crmMappingSchema,
  crmProviderPresets,
} from "./modal";
import MyDiv from "./CrmMapping.style";


const AVAILABLE_TOKENS = [
  { token: "{{empName}}", label: "Full Name" },
  { token: "{{firstName}}", label: "First Name" },
  { token: "{{lastName}}", label: "Last Name" },
  { token: "{{email}}", label: "Official Email" },
  { token: "{{phone}}", label: "Phone" },
  { token: "{{employeeCode}}", label: "Employee Code" },
  { token: "{{designation}}", label: "Designation" },
  { token: "{{department}}", label: "Department" },
  { token: "{{branch}}", label: "Branch" },
  { token: "{{company}}", label: "Company" },
  { token: "{{joiningDate}}", label: "Joining Date" },
  { token: "{{managerName}}", label: "Manager Name" },
  { token: "{{managerEmail}}", label: "Manager Email" },
  { token: "{{timestamp}}", label: "Timestamp" },
];

export default function CrmMapping() {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [tableData, setTableData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedData, setSelectedData] = useState<any>(null);

  // Dropdown lists (normalized with id & name)
  const [companies, setCompanies] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);

  // Multiselect form states
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);

  // Filter bar states
  const [filterCompany, setFilterCompany] = useState<string>("");
  const [filterBranch, setFilterBranch] = useState<string>("");
  const [filterDepartment, setFilterDepartment] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");

  // API Testing states
  const [testingApi, setTestingApi] = useState(false);
  const [testEnv, setTestEnv] = useState<"stage" | "live">("stage");
  const [apiTestResult, setApiTestResult] = useState<{
    success: boolean;
    statusCode: number;
    statusText?: string;
    latencyMs?: number;
    message?: string;
    responsePreview?: string;
  } | null>(null);

  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0,
    rows: 20,
    sortField: undefined,
    sortOrder: undefined,
    multiSortMeta: [],
    filters: {},
    globalFilter: "",
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ICrmMapping>({
    resolver: yupResolver(crmMappingSchema) as any,
    defaultValues: emptyCrmMapping,
    mode: "onBlur",
  });

  const watchedStageUrl = watch("stage_Url") || "";
  const watchedLiveUrl = watch("live_Url") || "";
  const watchedEndpoint = watch("endpoint") || "";
  const watchedMethod = watch("http_Method") || "POST";
  const watchedAuthType = watch("auth_Type") || "Bearer";
  const watchedCrmName = watch("crm_Name") || "";

  // Load dropdown lists
  const loadDropdowns = async () => {
    const compRes = await fetchApi("Dropdown/company", "GET");
    if (compRes && Array.isArray(compRes)) {
      setCompanies(
        compRes.map((c: any) => ({
          ...c,
          id: c.company_ID || c.id,
          name: c.company_Name || c.name || `Company #${c.id}`,
        }))
      );
    }

    const deptRes = await fetchApi("DropDown/department", "GET");
    if (deptRes && Array.isArray(deptRes)) {
      setDepartments(
        deptRes.map((d: any) => ({
          ...d,
          id: d.id || d.department_Id,
          name: d.departmentName || d.name || `Dept #${d.id}`,
        }))
      );
    }

    const branchRes = await fetchApi("DropDown/branchesss", "GET");
    if (branchRes && Array.isArray(branchRes)) {
      setBranches(
        branchRes.map((b: any) => ({
          ...b,
          id: b.branch_Id || b.branchId || b.id,
          name: b.branchName || b.branch_Name || b.name || `Branch #${b.id}`,
        }))
      );
    }
  };

  // Dynamic branch reload if single company selected
  useEffect(() => {
    if (selectedCompanies.length === 1) {
      fetchApi(`DropDown/roleWiseBranch?companyId=${selectedCompanies[0]}`, "GET").then((res) => {
        if (res && Array.isArray(res) && res.length > 0) {
          setBranches(
            res.map((b: any) => ({
              ...b,
              id: b.branch_Id || b.branchId || b.id,
              name: b.branchName || b.branch_Name || b.name,
            }))
          );
        }
      });
    }
  }, [selectedCompanies]);

  // Load table data
  const loadData = async () => {
    setLoading(true);
    let query = `first=${lazyParams.first}&rows=${lazyParams.rows}`;

    if (filterCompany) query += `&companyId=${filterCompany}`;
    if (filterBranch) query += `&branchId=${filterBranch}`;
    if (filterDepartment) query += `&departmentId=${filterDepartment}`;
    if (filterStatus !== "") query += `&isActive=${filterStatus === "active"}`;
    if (lazyParams.globalFilter) query += `&globalFilter=${encodeURIComponent(lazyParams.globalFilter)}`;

    const response = await fetchApi(`CrmMapping?${query}`, "GET");
    if (response) {
      setTableData(response.data || []);
      setTotalCount(response.totalRecords || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDropdowns();
  }, []);

  useEffect(() => {
    loadData();
  }, [lazyParams, filterCompany, filterBranch, filterDepartment, filterStatus]);

  const handleAddNew = () => { 
    setIsEditMode(false);
    setSelectedData(null);
    setSelectedCompanies([]);
    setSelectedBranches([]);
    setSelectedDepartments([]);
    setApiTestResult(null);
    reset(emptyCrmMapping);
    onOpen();
  };

  const onEdit = (rowData: any) => {
    setIsEditMode(true);
    setSelectedData(rowData);
    setSelectedCompanies(rowData.company_Id ? [rowData.company_Id] : []);
    setSelectedBranches(rowData.branch_Id ? [rowData.branch_Id] : []);
    setSelectedDepartments(rowData.department_Id ? [rowData.department_Id] : []);
    setApiTestResult(null);

    reset({
      company_Id: rowData.company_Id,
      branch_Id: rowData.branch_Id || null,
      department_Id: rowData.department_Id || null,
      crm_Name: rowData.crm_Name || "",
      stage_Url: rowData.stage_Url || "",
      live_Url: rowData.live_Url || "",
      endpoint: rowData.endpoint || "",
      roles_Endpoint: rowData.roles_Endpoint || "/api/roles",
      http_Method: rowData.http_Method || "POST",
      auth_Type: rowData.auth_Type || "Bearer",
      auth_Token: rowData.auth_Token || "",
      username: rowData.username || "",
      password: rowData.password || "",
      headers_Json: rowData.headers_Json || "",
      payload_Template: rowData.payload_Template || "",
      description: rowData.description || "",
      is_Active: rowData.is_Active !== false,
    });

    onOpen();
  };

  const onDelete = async (rowData: any) => {
    if (!window.confirm(`Are you sure you want to delete CRM mapping for ${rowData.crm_Name}?`)) {
      return;
    }
    const response = await fetchApi(`CrmMapping/${rowData.id}`, "DELETE");
    if (response) {
      addToast({ message: "CRM mapping deleted successfully", status: "success" });
      loadData();
    }
  };

  const onToggleStatus = async (rowData: any) => {
    const response = await fetchApi(`CrmMapping/${rowData.id}/toggle-status`, "PUT");
    if (response) {
      addToast({ message: response.message || "Status updated", status: "success" });
      loadData();
    }
  };

    const handleTemplateSelect = (presetName: string) => {
    const preset = crmProviderPresets.find((p) => p.name === presetName);
    if (preset) {
      setValue("crm_Name", preset.name);
      setValue("auth_Type", preset.defaultAuthType);
      if (preset.defaultStageUrl) setValue("stage_Url", preset.defaultStageUrl);
      if (preset.defaultLiveUrl) setValue("live_Url", preset.defaultLiveUrl);
      if (preset.defaultEndpoint) setValue("endpoint", preset.defaultEndpoint);
    }
  };

  // Test API Probe
  const handleTestApi = async () => {
    const targetUrl = testEnv === "live" ? watchedLiveUrl : watchedStageUrl;

    if (!targetUrl || !targetUrl.trim()) {
      addToast({
        message: `Please enter a valid ${testEnv === "live" ? "Live" : "Staging"} URL to test`,
        status: "warning",
      });
      return;
    }

    setTestingApi(true);
    setApiTestResult(null);

    const testPayload = {
      targetUrl: targetUrl.trim(),
      endpoint: watchedEndpoint || "/api/v1/users/provision",
      http_Method: watchedMethod || "POST",
      auth_Type: watchedAuthType,
      auth_Token: watch("auth_Token") || "",
      username: watch("username") || "",
      password: watch("password") || "",
    };

    const res = await fetchApi("CrmMapping/test-connection", "POST", testPayload);
    if (res) {
      setApiTestResult(res);
      addToast({
        message: res.message || "Endpoint connection test completed",
        status: res.success ? "success" : "warning",
      });
    } else {
      setApiTestResult({
        success: false,
        statusCode: 0,
        message: "Failed to connect to endpoint or network timeout",
      });
    }
    setTestingApi(false);
  };

    const onFormError = (formErrors: any) => {
    console.error("CRM Mapping Validation Errors:", formErrors);
    const errorKeys = Object.keys(formErrors);
    if (errorKeys.length > 0) {
      const firstKey = errorKeys[0];
      const errorMsg = formErrors[firstKey]?.message || `Please check ${firstKey}`;
      addToast({
        message: `${errorMsg}`,
        status: "error",
      });
    }
  };

  const onSubmit = async (data: ICrmMapping) => {
    if (selectedCompanies.length === 0) {
      addToast({ message: "Please select at least one Company", status: "error" });
      return;
    }

    setLoading(true);

    const cleanEndpoint = "/" + (data.endpoint ? data.endpoint.trim().replace(/^\/+/, "") : "api/v1/users/provision");
    const cleanRolesEndpoint = "/" + (data.roles_Endpoint ? data.roles_Endpoint.trim().replace(/^\/+/, "") : "api/roles");
    const cleanStage = data.stage_Url.trim().replace(/\/+$/, "");
    const cleanLive = data.live_Url.trim().replace(/\/+$/, "");

    const payload = {
      ...data,
      company_Id: selectedCompanies[0],
      branch_Id: selectedBranches[0] || null,
      department_Id: selectedDepartments[0] || null,
      company_Ids: selectedCompanies,
      branch_Ids: selectedBranches,
      department_Ids: selectedDepartments,
      endpoint: cleanEndpoint,
      roles_Endpoint: cleanRolesEndpoint,
      stage_Url: cleanStage,
      live_Url: cleanLive,
    };

    const method = isEditMode ? "PUT" : "POST";
    const url = isEditMode ? `CrmMapping/${selectedData.id}` : "CrmMapping";

    const response = await fetchApi(url, method, payload);
    if (response) {
      addToast({
        message: response.message || `CRM mapping ${isEditMode ? "updated" : "saved"} successfully`,
        status: "success",
      });
      loadData();
      onClose();
    }
    setLoading(false);
  };

  const activeCount = tableData.filter((x) => x.is_Active).length;

  // Resolved URL preview calculation
  const resolvedTargetBase = testEnv === "live" ? watchedLiveUrl : watchedStageUrl;
  const resolvedCleanBase = (resolvedTargetBase || "").trim().replace(/\/+$/, "");
  const resolvedCleanEndpoint = watchedEndpoint
    ? "/" + watchedEndpoint.trim().replace(/^\/+/, "")
    : "/api/v1/users/provision";
  const fullPreviewUrl = resolvedCleanBase ? `${resolvedCleanBase}${resolvedCleanEndpoint}` : `https://your-crm-domain.com${resolvedCleanEndpoint}`;

  return (
    <MyDiv>
      <Box className="page_wrapper">
        {/* Header */}
        <Flex className="page_heading flex_header" mb={4}>
          <Box>
            <Text className="font-poppins text_semibold text_3xl">CRM Mapping Master</Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Route employee provisioning and user profile synchronizations to targeted CRM instances
            </Text>
          </Box>
          <Flex gap={2}>
            <CustomButton
              title="Add CRM Mapping"
              className="btn"
              onClick={handleAddNew}
            />
          </Flex>
        </Flex>

        {/* Clean Stat Metric Cards */}
        <Grid templateColumns={{ base: "repeat(1, 1fr)", sm: "repeat(3, 1fr)" }} gap={4} mb={4}>
          <GridItem>
            <Box className="metric-card">
              <Text fontSize="xs" fontWeight="600" color="gray.500">
                Total Mappings
              </Text>
              <Text fontSize="2xl" fontWeight="700" color="#006838" mt={1}>
                {totalCount}
              </Text>
            </Box>
          </GridItem>
          <GridItem>
            <Box className="metric-card">
              <Text fontSize="xs" fontWeight="600" color="gray.500">
                Active Mappings
              </Text>
              <Text fontSize="2xl" fontWeight="700" color="#16a34a" mt={1}>
                {activeCount}
              </Text>
            </Box>
          </GridItem>
          <GridItem>
            <Box className="metric-card">
              <Text fontSize="xs" fontWeight="600" color="gray.500">
                Inactive Mappings
              </Text>
              <Text fontSize="2xl" fontWeight="700" color="#dc2626" mt={1}>
                {totalCount - activeCount}
              </Text>
            </Box>
          </GridItem>
        </Grid>

        {/* Filter Section */}
        <Flex gap={3} mb={4} wrap="wrap" align="center">
          <Select
            placeholder="All Companies"
            size="sm"
            maxW="200px"
            bg="white"
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select
            placeholder="All Branches"
            size="sm"
            maxW="200px"
            bg="white"
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>

          <Select
            placeholder="All Departments"
            size="sm"
            maxW="200px"
            bg="white"
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            maxW="140px"
            bg="white"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>

          {(filterCompany || filterBranch || filterDepartment || filterStatus) && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => {
                setFilterCompany("");
                setFilterBranch("");
                setFilterDepartment("");
                setFilterStatus("");
              }}
            >
              Clear
            </Button>
          )}
        </Flex>

        {/* CustomTables Data Table */}
        <Grid className="grid_container gap-4">
          <GridItem colSpan={12}>
            <CustomTables<any>
              value={tableData}
              loading={loading}
              emptyMessage="No CRM mappings found"
              preferenceKey="crmMappingTable"
              lazy
              lazyParams={lazyParams}
              onLazyParamsChange={setLazyParams}
              totalRecords={totalCount}
              showGlobalSearch={false}
              showToolbar={true}
              showPagination
              rowsPerPageOptions={[20, 50, 100]}
              maxTableHeight={null}
            >
              <Column header="S.No" body={(_, index) => lazyParams.first + index + 1} />
              <Column header="Company" field="company_Name" sortable filterable filterType="text" />
              <Column
                header="Branch"
                body={(rowData: any) =>
                  rowData.branch_Name ? rowData.branch_Name : <Text color="gray.400">All Branches</Text>
                }
              />
              <Column
                header="Department"
                body={(rowData: any) =>
                  rowData.department_Name ? rowData.department_Name : <Text color="gray.400">All Departments</Text>
                }
              />
              <Column header="CRM Name" field="crm_Name" sortable filterable filterType="text" />
              <Column
                header="Endpoint"
                body={(rowData: any) => (
                  <Text fontFamily="monospace" fontSize="xs" color="#0369a1">
                    {rowData.http_Method || "POST"} {rowData.endpoint}
                  </Text>
                )}
              />
              <Column
                header="Staging URL"
                body={(rowData: any) => <Text className="url-text">{rowData.stage_Url}</Text>}
              />
              <Column
                header="Live URL"
                body={(rowData: any) => <Text className="url-text">{rowData.live_Url}</Text>}
              />
              <Column
                header="Status"
                body={(rowData: any) => (
                  <Switch
                    colorScheme="green"
                    isChecked={rowData.is_Active !== false}
                    onChange={() => onToggleStatus(rowData)}
                  />
                )}
              />
              <Column
                header="Action"
                body={(rowData: any) => (
                  <HStack spacing={1}>
                    <IconButton
                      aria-label="Edit"
                      icon={<EditIcon />}
                      size="sm"
                      colorScheme="orange"
                      variant="ghost"
                      onClick={() => onEdit(rowData)}
                    />
                    <IconButton
                      aria-label="Delete"
                      icon={<DeleteIcon />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => onDelete(rowData)}
                    />
                  </HStack>
                )}
              />
            </CustomTables>
          </GridItem>
        </Grid>

        {/* ✅ BALANCED PROFESSIONAL FORM DRAWER WITH PREVIEW & API TEST */}
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
          <DrawerOverlay />
          <DrawerContent maxW={{ base: "100%", md: "680px" }}>
            <DrawerHeader borderBottomWidth="1px" fontSize="md" fontWeight="600" bg="#f8fafc" py={3}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="md" fontWeight="700" color="#1e293b">
                    {isEditMode ? "Edit CRM Mapping" : "Configure CRM Mapping"}
                  </Text>
                  <Text fontSize="xs" fontWeight="normal" color="gray.500">
                    Map organizational entities to target CRM endpoints with live testing
                  </Text>
                </Box>
                {watchedCrmName && (
                  <Badge colorScheme="green" fontSize="xs" px={2} py={0.5} borderRadius="full">
                    {watchedCrmName}
                  </Badge>
                )}
              </Flex>
            </DrawerHeader>

            <DrawerBody py={5} px={6}>
              <Box as="form" id="crm-form" onSubmit={handleSubmit(onSubmit)}>
                {/* 1. Quick Presets (Optional) */}
                {!isEditMode && (
                  <Box mb={5} p={3} bg="#f1f5f9" borderRadius="8px" border="1px dashed #cbd5e1">
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontSize="xs" fontWeight="700" color="#475569" textTransform="uppercase" letterSpacing="0.5px">
                        Quick Preset Template
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Optional: Auto-fills standard endpoints
                      </Text>
                    </Flex>
                    <Select
                      placeholder="Select a CRM preset template..."
                      size="sm"
                      bg="white"
                      borderColor="#cbd5e1"
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                    >
                      {crmProviderPresets.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name} — {p.description}
                        </option>
                      ))}
                    </Select>
                  </Box>
                )}

                {/* 2. Organization Target Scope (MultiSelect) */}
                <Box mb={5}>
                  <Text fontSize="xs" fontWeight="700" color="#006838" textTransform="uppercase" letterSpacing="0.5px" mb={3}>
                    1. Target Scope (Multi-Select)
                  </Text>

                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={3}>
                    <GridItem>
                      <MultiSelectMenu
                        isRequired
                        label="Companies"
                        placeholder="Select Company(s)"
                        options={companies}
                        selectedValues={selectedCompanies}
                        setSelectedValues={setSelectedCompanies}
                        labelKey="name"
                        valueKey="id"
                        colorScheme="green"
                        overflow
                        ShowNumberOfTiles={1}
                        mb={0}
                      />
                    </GridItem>

                    <GridItem>
                      <MultiSelectMenu
                        label="Branches"
                        placeholder="All Branches (Optional)"
                        options={branches}
                        selectedValues={selectedBranches}
                        setSelectedValues={setSelectedBranches}
                        labelKey="name"
                        valueKey="id"
                        colorScheme="purple"
                        overflow
                        ShowNumberOfTiles={1}
                        mb={0}
                      />
                    </GridItem>
                  </Grid>

                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                    <GridItem>
                      <MultiSelectMenu
                        label="Departments"
                        placeholder="All Departments (Optional)"
                        options={departments}
                        selectedValues={selectedDepartments}
                        setSelectedValues={setSelectedDepartments}
                        labelKey="name"
                        valueKey="id"
                        colorScheme="blue"
                        overflow
                        ShowNumberOfTiles={1}
                        mb={0}
                      />
                    </GridItem>

                    <GridItem>
                      <HrlenseInput
                        isRequired
                        label="CRM Service Name"
                        name="crm_Name"
                        placeholder="e.g. Teklense CRM, Bivocal, Kommuno"
                        control={control}
                        errors={errors}
                      />
                    </GridItem>
                  </Grid>
                </Box>

                {/* 3. Routing & Endpoint Configuration */}
                <Box mb={5}>
                  <Text fontSize="xs" fontWeight="700" color="#006838" textTransform="uppercase" letterSpacing="0.5px" mb={3}>
                    2. Routing URLs & Protocol
                  </Text>

                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={3}>
                    <GridItem>
                      <HrlenseInput
                        isRequired
                        label="Staging Base URL"
                        name="stage_Url"
                        placeholder="https://stage-api.domain.com"
                        control={control}
                        errors={errors}
                      />
                    </GridItem>

                    <GridItem>
                      <HrlenseInput
                        isRequired
                        label="Live Base URL"
                        name="live_Url"
                        placeholder="https://api.domain.com"
                        control={control}
                        errors={errors}
                      />
                    </GridItem>
                  </Grid>

                  <Grid templateColumns={{ base: "1fr", md: "2.5fr 1.5fr" }} gap={4}>
                    <GridItem>
                      <HrlenseInput
                        isRequired
                        label="Provisioning Endpoint"
                        name="endpoint"
                        placeholder="/api/v1/users/provision"
                        control={control}
                        errors={errors}
                      />
                    </GridItem>

                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm" fontWeight="500">
                          HTTP Method
                        </FormLabel>
                        <Controller
                          name="http_Method"
                          control={control}
                          render={({ field }) => (
                            <Select {...field} size="md" bg="white">
                              <option value="POST">POST</option>
                              <option value="PUT">PUT</option>
                              <option value="PATCH">PATCH</option>
                              <option value="GET">GET</option>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>

                  <Box mt={3}>
                    <HrlenseInput
                      label="CRM Roles Endpoint (Dynamic Role Mapping)"
                      name="roles_Endpoint"
                      placeholder="/api/roles"
                      control={control}
                      errors={errors}
                      
                    />
                  </Box>
                </Box>

                {/* 4. Authentication Configuration */}
                <Box mb={5}>
                  <Text fontSize="xs" fontWeight="700" color="#006838" textTransform="uppercase" letterSpacing="0.5px" mb={3}>
                    3. Security & Credentials
                  </Text>

                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm" fontWeight="500">
                          Auth Scheme
                        </FormLabel>
                        <Controller
                          name="auth_Type"
                          control={control}
                          render={({ field }) => (
                            <Select {...field} size="md" bg="white">
                              <option value="Bearer">Bearer Token (JWT)</option>
                              <option value="ApiKey">API Key Header</option>
                              <option value="Basic">Basic Auth (User & Pass)</option>
                              <option value="None">None / Open</option>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      {watchedAuthType === "Basic" ? (
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          <GridItem>
                            <HrlenseInput
                              isRequired
                              label="Username"
                              name="username"
                              placeholder="Username"
                              control={control}
                              errors={errors}
                            />
                          </GridItem>
                          <GridItem>
                            <HrlenseInput
                              isRequired
                              label="Password"
                              name="password"
                              type="password"
                              placeholder="Password"
                              control={control}
                              errors={errors}
                            />
                          </GridItem>
                        </Grid>
                      ) : watchedAuthType !== "None" ? (
                        <HrlenseInput
                          isRequired
                          label={watchedAuthType === "ApiKey" ? "API Key Secret" : "Bearer Token"}
                          name="auth_Token"
                          type="password"
                          placeholder={watchedAuthType === "ApiKey" ? "Enter X-API-KEY" : "Enter Bearer JWT"}
                          control={control}
                          errors={errors}
                        />
                      ) : (
                        <Box pt={7}>
                          <Text fontSize="xs" color="gray.500">
                            No credentials required for this endpoint.
                          </Text>
                        </Box>
                      )}
                    </GridItem>
                  </Grid>
                </Box>

                
                {/* 4. LIVE ROUTING PREVIEW & API TEST PROBE */}
                <Box
                  mb={5}
                  p={4}
                  bg="#f8fafc"
                  borderRadius="8px"
                  border="1px solid #e2e8f0"
                  boxShadow="sm"
                >
                  <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                    <HStack spacing={2}>
                      <Text fontSize="xs" fontWeight="700" color="#334155" textTransform="uppercase" letterSpacing="0.5px">
                        Resolved Route & Test Console
                      </Text>
                    </HStack>

                    {/* Environment toggle pill */}
                    <HStack spacing={1} bg="#e2e8f0" p={0.5} borderRadius="6px">
                      <Button
                        size="xs"
                        variant={testEnv === "stage" ? "solid" : "ghost"}
                        colorScheme={testEnv === "stage" ? "blue" : "gray"}
                        onClick={() => setTestEnv("stage")}
                        borderRadius="4px"
                        px={2.5}
                      >
                        Staging
                      </Button>
                      <Button
                        size="xs"
                        variant={testEnv === "live" ? "solid" : "ghost"}
                        colorScheme={testEnv === "live" ? "green" : "gray"}
                        onClick={() => setTestEnv("live")}
                        borderRadius="4px"
                        px={2.5}
                      >
                        Live
                      </Button>
                    </HStack>
                  </Flex>

                  {/* Monospace URL preview */}
                  <Box
                    bg="#0f172a"
                    color="#e2e8f0"
                    p={3}
                    borderRadius="6px"
                    mb={3}
                    fontSize="xs"
                    fontFamily="monospace"
                  >
                    <Flex align="center" gap={2} wrap="wrap">
                      <Badge colorScheme={watchedMethod === "POST" ? "green" : "purple"} fontSize="10px">
                        {watchedMethod}
                      </Badge>
                      <Text color="#38bdf8" wordBreak="break-all">
                        {fullPreviewUrl}
                      </Text>
                    </Flex>
                  </Box>

                  {/* Scope summary and Test Button */}
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <HStack spacing={2} fontSize="xs" color="gray.600">
                      <Badge variant="subtle" colorScheme="teal" fontSize="10px">
                        {selectedCompanies.length} Company{selectedCompanies.length === 1 ? "" : "ies"}
                      </Badge>
                      <Badge variant="subtle" colorScheme="purple" fontSize="10px">
                        {selectedBranches.length > 0 ? `${selectedBranches.length} Branch(es)` : "All Branches"}
                      </Badge>
                      <Badge variant="subtle" colorScheme="blue" fontSize="10px">
                        {selectedDepartments.length > 0 ? `${selectedDepartments.length} Dept(s)` : "All Depts"}
                      </Badge>
                    </HStack>

                    <Button
                      size="sm"
                      colorScheme="teal"
                      variant="solid"
                      isLoading={testingApi}
                      loadingText="Testing..."
                      onClick={handleTestApi}
                    >
                      ⚡ Test {testEnv === "live" ? "Live" : "Staging"} Endpoint
                    </Button>
                  </Flex>

                  {/* API Test Result Feedback */}
                  {apiTestResult && (
                    <Box
                      mt={3}
                      p={3}
                      borderRadius="6px"
                      bg={apiTestResult.success ? "#f0fdf4" : "#fef2f2"}
                      border={`1px solid ${apiTestResult.success ? "#bbf7d0" : "#fecaca"}`}
                    >
                      <Flex align="center" justify="space-between" mb={1}>
                        <HStack spacing={2}>
                          {apiTestResult.success ? (
                            <CheckCircleIcon color="green.500" />
                          ) : (
                            <WarningTwoIcon color="red.500" />
                          )}
                          <Text
                            fontSize="xs"
                            fontWeight="700"
                            color={apiTestResult.success ? "green.800" : "red.800"}
                          >
                            {apiTestResult.success ? "Connection Verified" : "Probe Failed"}
                          </Text>
                          {apiTestResult.statusCode > 0 && (
                            <Badge
                              colorScheme={apiTestResult.success ? "green" : "red"}
                              fontSize="10px"
                            >
                              HTTP {apiTestResult.statusCode}
                            </Badge>
                          )}
                        </HStack>

                        {apiTestResult.latencyMs !== undefined && (
                          <Badge variant="outline" colorScheme="gray" fontSize="10px">
                            ⚡ {apiTestResult.latencyMs} ms
                          </Badge>
                        )}
                      </Flex>

                      <Text fontSize="xs" color={apiTestResult.success ? "green.700" : "red.700"} mt={1}>
                        {apiTestResult.message}
                      </Text>

                      {apiTestResult.responsePreview && (
                        <Box
                          mt={2}
                          p={2}
                          bg="white"
                          borderRadius="4px"
                          fontSize="11px"
                          fontFamily="monospace"
                          color="#334155"
                          maxH="80px"
                          overflowY="auto"
                          border="1px solid #e2e8f0"
                        >
                          {apiTestResult.responsePreview}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* 6. Active Toggle */}
                <Box mb={2}>
                  <FormControl display="flex">
                    <Box>
                      <FormLabel mb="0" fontSize="sm" fontWeight="600">
                        Enable CRM Mapping
                      </FormLabel>
                      <Text fontSize="xs" color="gray.500">
                        When enabled, newly onboarded employees within target scope will be provisioned
                      </Text>
                    </Box>
                    <Controller
                      name="is_Active"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          colorScheme="green"
                          isChecked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  </FormControl>
                </Box>
              </Box>
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px" bg="#f8fafc" py={3}>
              <Button variant="outline" mr={3} size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                isLoading={loading}
                type="submit"
                onClick={handleSubmit(onSubmit, onFormError)}
                size="sm"
                className="font-poppins text_sm text_medium"
                style={{ backgroundColor: "#006838", color: "white" }}
              >
                {isEditMode ? "Save Changes" : "Create Mappings"}
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </MyDiv>
  );
}
